import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Route, Routes, useNavigate } from 'react-router-dom';
import {
    APP_PATHS,
    SHARED_API_URL,
    STUDENT_SUMMARY_PATHS,
} from '../../constants/shared/ApiConstants';

const { BENCHMARK, DUAL_LANGUAGE, HOME_CONNECT, INSTRUCTIONS, PROGRESS, STUDENT_SUMMARY } =
    APP_PATHS;
const { SS_BENCHMARK_HISTORY, SS_TRC_SUMMARY, SS_PROGRESS } = STUDENT_SUMMARY_PATHS;

// Use custom Navigate to avoid maximum update depth exceeded error:
// see here - https://github.com/salvoravida/redux-first-history/issues/80
// and here - https://github.com/salvoravida/redux-first-history/issues/81
export function Navigate({ to, replace, state }) {
    const navigate = useNavigate();
    useEffect(() => {
        navigate(to, { replace, state });
    }, []);

    return null;
}

Navigate.defaultProps = {
    to: '/',
    replace: false,
    state: null,
};

Navigate.propTypes = {
    to: PropTypes.string,
    replace: PropTypes.bool,
    state: PropTypes.any,
};

const AppRoutes = ({ licensingInfo, config }) => {
    const { appUrl, views, pageWrapperContainer: PageWrapperContainer } = config;
    const { benchmark, progress, instructions, homeconnect, duallanguage, studentsummary } = views;
    const { benchmarkHistory, trcSummary, progress: ssProgress } = studentsummary.childrens;

    const { ROOT_URL, APP_ROOT_URL, BENCHMARK_URL } = SHARED_API_URL('', appUrl).BASE_URLS;

    const BenchmarkContainer = benchmark.container;
    const ProgressContainer = progress.container;
    const InstructionContainer = instructions.container;
    const HomeConnectContainer = homeconnect.container;
    const DualLanguageContainer = duallanguage.container;
    const StudentSummaryContainer = studentsummary.container;
    const BenchmarkHistoryContainer = benchmarkHistory.container;
    const TrcSummaryContainer = trcSummary.container;
    const StudentSummaryProgressContainer = ssProgress.container;

    const isProgressRoute = progress.enabledWhen(licensingInfo);
    const isDualLanguageRoute = duallanguage.enabledWhen(licensingInfo);
    const isTrcSummaryRoute = trcSummary.enabledWhen(licensingInfo);

    return (
        <Routes>
            <Route path={ROOT_URL}>
                <Route index element={<Navigate to={BENCHMARK_URL} />} />
                <Route path={APP_ROOT_URL}>
                    <Route index element={<Navigate to={BENCHMARK_URL} />} />
                    <Route
                        path={BENCHMARK}
                        element={
                            <PageWrapperContainer>
                                <BenchmarkContainer />
                            </PageWrapperContainer>
                        }
                    />
                    <Route
                        path={PROGRESS}
                        element={
                            isProgressRoute ? (
                                <PageWrapperContainer>
                                    <ProgressContainer />
                                </PageWrapperContainer>
                            ) : (
                                <Navigate to={BENCHMARK_URL} />
                            )
                        }
                    />
                    <Route
                        path={INSTRUCTIONS}
                        element={
                            <PageWrapperContainer>
                                <InstructionContainer />
                            </PageWrapperContainer>
                        }
                    />
                    <Route
                        path={HOME_CONNECT}
                        element={
                            <PageWrapperContainer>
                                <HomeConnectContainer />
                            </PageWrapperContainer>
                        }
                    />
                    <Route
                        path={DUAL_LANGUAGE}
                        element={
                            isDualLanguageRoute ? (
                                <PageWrapperContainer>
                                    <DualLanguageContainer />
                                </PageWrapperContainer>
                            ) : (
                                <Navigate to={BENCHMARK_URL} />
                            )
                        }
                    />
                    <Route
                        path={STUDENT_SUMMARY}
                        element={
                            <PageWrapperContainer>
                                <StudentSummaryContainer />
                            </PageWrapperContainer>
                        }
                    >
                        <Route
                            path={SS_BENCHMARK_HISTORY}
                            element={<BenchmarkHistoryContainer />}
                        />
                        <Route
                            path={SS_TRC_SUMMARY}
                            element={
                                isTrcSummaryRoute ? (
                                    <TrcSummaryContainer />
                                ) : (
                                    <Navigate to={BENCHMARK_URL} />
                                )
                            }
                        />
                        <Route path={SS_PROGRESS} element={<StudentSummaryProgressContainer />} />
                    </Route>
                </Route>
            </Route>
            <Route path="*" element={<Navigate to={BENCHMARK_URL} />} />
        </Routes>
    );
};

AppRoutes.defaultProps = {
    licensingInfo: [],
};
AppRoutes.propTypes = {
    licensingInfo: PropTypes.array,
    config: PropTypes.object.isRequired,
};

export default AppRoutes;
